import {filter, findIndex, map} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {DragDropContext, Droppable} from '@hello-pangea/dnd';
import EmptyPlaceholder from 'shared/college-earnings/components/empty-placeholder';
import FollowingHeader from 'shared/college-earnings/components/following-header';
import LoadingIndicator from 'shared/college-earnings/components/loading-indicator';
import PlaceholderText from 'shared/college-earnings/components/placeholder-text';
import DiscoverCollegesButton from 'shared/components/discover-colleges-button';
import {getMedia, watchMedia} from 'shared/mq';
import {track} from 'src/utils/analytics';
import OnTrack from 'high-school-student/on-track/containers/on-track';
import Earning from './earning';

const Raise = window.Raise;

class CollegeEarnings extends React.Component {
  static propTypes = {
    earnings: PropTypes.array,
    previousEarnings: PropTypes.array,
    loading: PropTypes.bool,
    animating: PropTypes.bool,
    showBuildYourListOnTrack: PropTypes.bool.isRequired,
    showNewEarnings: PropTypes.bool,
    receiveEarnings: PropTypes.func.isRequired,
    receiveInitialEarnings: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    followedCollegeIds: PropTypes.array,
    receiveEarningsOrder: PropTypes.func.isRequired,
    updateEarningsOrder: PropTypes.func.isRequired,
    setEarningFilter: PropTypes.func.isRequired,
    studentId: PropTypes.string,
    earningFilter: PropTypes.string.isRequired,
    showFinancialAidAwardLettersLink: PropTypes.bool,
    fetchFeatureFlags: PropTypes.func.isRequired,
  };

  componentDidMount() {
    const {
      setLoading,
      receiveEarnings,
      receiveInitialEarnings,
      followedCollegeIds,
      fetchFeatureFlags,
    } = this.props;
    this.empty = followedCollegeIds ? !followedCollegeIds.length : false;

    if (this.empty) {
      setLoading(false);
    } else {
      fetchFeatureFlags();
      receiveInitialEarnings(10);
    }

    Raise.vent.on('eval:followed:colleges', () => {
      receiveEarnings();
    });

    this.mq = watchMedia(() => {
      this.forceUpdate();
    });
  }

  componentWillUnmount() {
    this.mq.remove();
  }

  onDragEnd = (result) => {
    const {earnings, receiveEarningsOrder, updateEarningsOrder, studentId} = this.props;
    const {destination, source} = result;

    // Don't save if the user drags the element to an invalid location.
    if (!destination) return;

    // Don't save if the user drags the element to its current location.
    if (destination.droppableId === source.droppableId && destination.index === source.index)
      return;

    const earningsOrder = earnings.slice();
    earningsOrder.splice(source.index, 1);
    earningsOrder.splice(destination.index, 0, earnings[source.index]);

    track('earnings-sidebar-order-event', {
      order: JSON.stringify(map(earningsOrder, '_id')),
      studentId,
    });

    receiveEarningsOrder(earningsOrder);
    updateEarningsOrder(earningsOrder);
  };

  render() {
    const mq = getMedia();

    const {
      earnings,
      loading,
      previousEarnings,
      animating,
      showNewEarnings,
      showBuildYourListOnTrack,
      followedCollegeIds,
      setEarningFilter,
      earningFilter,
      showFinancialAidAwardLettersLink,
    } = this.props;

    const wrap = (nodes) => <div className="college-earnings">{nodes}</div>;

    if (loading) {
      return wrap(<LoadingIndicator />);
    }

    if (this.empty) {
      return wrap(<EmptyPlaceholder />);
    }

    const EARNINGS_LENGTH_BORDER_NEEDED = 6;

    const earningNodesMapper = (earningNodesList) =>
      map(earningNodesList, (earning, i) => {
        const prevEarningIndex = findIndex(previousEarnings, ['slug', earning.slug]);
        return (
          <Earning
            key={earning._id}
            earnings={earning}
            disableDrag={earningFilter.toLowerCase() !== 'all'}
            previousEarnings={prevEarningIndex === -1 ? null : previousEarnings[prevEarningIndex]}
            animating={animating}
            showNewEarnings={showNewEarnings}
            showBorderBottom={
              i < earningNodesList.length - 1 ||
              earningNodesList.length < EARNINGS_LENGTH_BORDER_NEEDED
            }
            firstChild={i === 0}
            lastChild={i === earningNodesList.length - 1}
            showBuildYourListOnTrack={showBuildYourListOnTrack}
            index={i}
          />
        );
      });

    const upcomingDeadlines = filter(
      earnings,
      (earning) => earning.earningRelationship.statusType === 'earning_deadline_soon'
    );

    const upcomingDeadlinesEarnings = earningNodesMapper(upcomingDeadlines);
    const earningNodes = earningNodesMapper(earnings);

    const placeholderText = (
      <PlaceholderText>
        All colleges you are following have upcoming earning deadlines.
      </PlaceholderText>
    );

    const simpleHeader = (
      <div className="college-earnings-header">
        <h1 className="college-earnings-header-text">Upcoming earning deadlines</h1>
        <span className="follow-count">{upcomingDeadlinesEarnings.length}</span>
      </div>
    );

    const collegesWithUpcomingDeadlines = (
      <>
        {simpleHeader}
        {upcomingDeadlinesEarnings}
      </>
    );

    return (
      <DragDropContext key="earnings" onDragEnd={this.onDragEnd}>
        <Droppable droppableId="portfolio-earnings-sidebar">
          {(provided) => (
            <div
              className="college-earnings-scroll"
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {mq.SM_AND_UP && <OnTrack />}
              {upcomingDeadlinesEarnings.length > 0 && collegesWithUpcomingDeadlines}
              <FollowingHeader
                key="header"
                followCount={(followedCollegeIds || []).length}
                {...{
                  setEarningFilter,
                  earningFilter,
                  showBuildYourListOnTrack,
                  showFinancialAidAwardLettersLink,
                }}
              />
              {earningNodes.length > 0 ? earningNodes : placeholderText}
              {provided.placeholder}
              {earningFilter !== 'not earning' && (followedCollegeIds || []).length < 6 && (
                <DiscoverCollegesButton />
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }
}

export default CollegeEarnings;
